import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "../components/Layout";
import CaseContextHeader from "../components/CaseContextHeader";
import StatusBadge from "../components/StatusBadge";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { CheckCircle2, RefreshCw } from "lucide-react";

const PIPELINE = [
  "File accepted", "OCR / text extraction", "Cleaning and segmentation",
  "Section detection", "Field extraction", "Validation",
];

export default function ProcessingReview() {
  const params = useParams();
  const [caseId, setCaseId] = useState(params.caseId || "");
  const [cases, setCases] = useState([]);
  const [caseDoc, setCaseDoc] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [blocks, setBlocks] = useState([]);

  const reload = () => {
    if (!caseId) return;
    api.get(`/cases/${caseId}`).then((r) => setCaseDoc(r.data.case));
    api.get(`/cases/${caseId}/processing`).then((r) => {
      setJobs(r.data.jobs);
      setBlocks(r.data.text_blocks);
    });
  };
  useEffect(() => {
    if (!caseId) {
      api.get("/cases").then((r) => setCases(r.data));
    } else {
      reload();
    }
  }, [caseId]);

  if (!caseId) {
    return (
      <Layout breadcrumbs={["SSRD", "Processing"]}>
        <h1 className="text-2xl font-bold mb-4">Pick a case</h1>
        <Card><CardContent className="p-0"><Table>
          <TableHeader><TableRow><TableHead>Case</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {cases.map((c) => (
              <TableRow key={c.case_id}>
                <TableCell className="font-mono text-xs">{c.case_number}/{c.case_year} — {c.district}</TableCell>
                <TableCell><Button size="sm" variant="outline" onClick={() => setCaseId(c.case_id)}>Open</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table></CardContent></Card>
      </Layout>
    );
  }

  return (
    <Layout breadcrumbs={[
      { label: "SSRD", to: "/dashboard" },
      { label: "Cases", to: "/cases" },
      caseDoc ? { label: `${caseDoc.case_number}/${caseDoc.case_year}`, to: `/cases/${caseId}` } : "…",
      "Processing",
    ]}>
      <CaseContextHeader caseDoc={caseDoc} />

      <Card className="mb-6"><CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Processing Pipeline</div>
            <h2 className="text-lg font-semibold">Document Processing Layer</h2>
          </div>
          <Button variant="outline" size="sm" onClick={reload} className="gap-2" data-testid="processing-refresh-btn"><RefreshCw className="size-4" /> Refresh</Button>
        </div>
        <ol className="flex flex-wrap items-center gap-2">
          {PIPELINE.map((s, i) => (
            <li key={s} className="flex items-center gap-2">
              <span className="rounded-md border border-border bg-secondary/40 px-3 py-1 text-xs">
                <span className="font-mono text-[10px] text-muted-foreground mr-1.5">{i + 1}</span> {s}
              </span>
            </li>
          ))}
        </ol>
      </CardContent></Card>

      <Card className="mb-6" data-testid="processing-jobs-card"><CardContent className="p-0">
        <div className="px-5 py-3 border-b border-border"><div className="text-lg font-semibold">Jobs</div></div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Document</TableHead><TableHead>Engine</TableHead>
            <TableHead>Pages</TableHead><TableHead>OCR Conf</TableHead>
            <TableHead>Status</TableHead><TableHead>Started</TableHead><TableHead>Completed</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {jobs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6 text-sm">No processing jobs yet.</TableCell></TableRow>}
            {jobs.map((j) => (
              <TableRow key={j.processing_job_id} data-testid={`job-row-${j.processing_job_id}`}>
                <TableCell className="font-mono text-[11px]">{j.document_id.slice(0, 8)}</TableCell>
                <TableCell className="text-xs">{j.processing_engine}</TableCell>
                <TableCell className="text-xs">{j.page_count ?? "—"}</TableCell>
                <TableCell className="text-xs">{j.ocr_confidence ?? "—"}</TableCell>
                <TableCell><StatusBadge status={j.processing_status} /></TableCell>
                <TableCell className="text-xs text-muted-foreground">{(j.started_at || "").replace("T", " ").slice(0, 16)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{(j.completed_at || "").replace("T", " ").slice(0, 16)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <div>
        <div className="text-[10px] tracking-widest text-muted-foreground uppercase mb-2">Extracted Text Blocks</div>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="text-blocks-grid">
          {blocks.length === 0 && <div className="text-sm text-muted-foreground col-span-3">Run OCR on a document to see extracted text blocks here.</div>}
          {blocks.map((b) => (
            <Card key={b.text_block_id}><CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-primary">{b.block_type.replace(/_/g, " ")}</div>
                <StatusBadge status={b.review_status} />
              </div>
              <div className="text-[11px] text-muted-foreground mb-2">Page {b.page_no} · confidence {b.extraction_confidence}</div>
              <div className="gujarati text-sm whitespace-pre-wrap max-h-44 overflow-y-auto">{b.block_text}</div>
            </CardContent></Card>
          ))}
        </div>
      </div>
    </Layout>
  );
}
